import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import DynamicSEO from "./components/DynamicSEO";
import ErrorPage from "./pages/ErrorPage";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Contact from "./pages/Contact";
import OrderForm from "./pages/OrderForm";
import OrderConfirmation from "./pages/OrderConfirmation";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Admin components
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import AddProduct from "./pages/admin/AddProduct";
import EditProduct from "./pages/admin/EditProduct";
import AdminCategories from "./pages/admin/Categories";
import OrderManagement from "./pages/admin/OrderManagement";
import SettingsManagement from "./pages/admin/SettingsManagement";
import AdminLayout from "./components/admin/AdminLayout";
import ScrollToTop from "./components/ScrollToTop";

const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <DynamicSEO />
        <ErrorBoundary>
          <div className="min-h-screen bg-gradient-to-br from-[#B7E5CD]/10 to-[#8ABEB9]/10">
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  <>
                    <Navbar />
                    <Home />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/shop"
                element={
                  <>
                    <Navbar />
                    <Shop />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/product/:id"
                element={
                  <>
                    <Navbar />
                    <ProductDetail />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/contact"
                element={
                  <>
                    <Navbar />
                    <Contact />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/order/:id"
                element={
                  <>
                    <Navbar />
                    <OrderForm />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/order-confirmation"
                element={<OrderConfirmation />}
              />
              <Route
                path="/terms"
                element={
                  <>
                    <Navbar />
                    <TermsOfService />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/privacy"
                element={
                  <>
                    <Navbar />
                    <PrivacyPolicy />
                    <Footer />
                  </>
                }
              />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminProducts />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/products/add"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AddProduct />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/products/edit/:id"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <EditProduct />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminCategories />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <OrderManagement />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <SettingsManagement />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                }
              />

              {/* Redirect /admin to /admin/dashboard */}
              <Route
                path="/admin"
                element={<Navigate to="/admin/dashboard" />}
              />

              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<ErrorPage />} />
            </Routes>
            <Toaster position="top-right" />
          </div>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
};

export default App;
